import React from 'react';
import { buildMappableAddressCoord } from '../../../helpers/api/addresses';
import { SearchedSpace } from './SearchResultsProvider';
import { useFilteredSearchResults } from './FilteredSearchResultsProvider';
import { MappableItem } from '../../../components/GoogleMap';

export const MappableVenuesContext = React.createContext<{
  mappableVenues: MappableItem[];
}>({
  mappableVenues: [],
});

// Maps a venue and its spaces to a MappableItem
const mapFilteredVenueToMappableVenue = (
  venue: SearchedSpace['venue'],
  spaces: SearchedSpace[],
) => ({
  key: venue.slug,
  name: venue.name,
  position: buildMappableAddressCoord(venue.address || {}),
  spaces: spaces.map((space) => ({
    name: space.name,
    venue: {
      name: venue.name,
      priceCategory: venue.priceCategory,
    },
    standing: space.standing || undefined,
    seated: space.seated || undefined,
    images: space.images.filter(
      (image): image is { url: string } => !!image.url,
    ),
  })),
  address: venue.address || undefined,
  handleClick: () => window.open(`/venues/${venue.slug}`),
});

export const MappableVenuesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { filteredSpaces, filteredVenues } = useFilteredSearchResults();

  const mappableVenues = React.useMemo(() => {
    return filteredVenues.map((venue) =>
      mapFilteredVenueToMappableVenue(
        venue,
        filteredSpaces.filter((space) => space.venue.slug === venue.slug),
      ),
    );
  }, [filteredSpaces, filteredVenues]);

  return (
    <MappableVenuesContext.Provider value={{ mappableVenues }}>
      {children}
    </MappableVenuesContext.Provider>
  );
};

export const useMappableVenues = () => React.useContext(MappableVenuesContext);
